import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, MousePointerClick, UserPlus, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';

interface InfluencerCode {
  id: string;
  ref_code: string;
  label: string;
  clicks: number;
  signups: number;
  created_at: string;
}

export default function AdminInfluencerDashboard() {
  const [codes, setCodes] = useState<InfluencerCode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from('influencer_codes')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (data) setCodes(data as unknown as InfluencerCode[]);
        setLoading(false);
      });
  }, []);

  const totalClicks = codes.reduce((s, c) => s + c.clicks, 0);
  const totalSignups = codes.reduce((s, c) => s + c.signups, 0);
  const conversionRate = totalClicks > 0 ? ((totalSignups / totalClicks) * 100).toFixed(1) : '0';

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/95 backdrop-blur">
        <div className="container flex h-16 items-center gap-4">
           <Link to="/admin" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Admin Hub</span>
          </Link>
          <h1 className="text-lg font-bold text-foreground">Influencer Analytics</h1>
        </div>
      </header>

      <main className="container py-8 space-y-6 max-w-4xl">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <MousePointerClick className="h-5 w-5 text-primary mx-auto mb-1" />
              <p className="text-2xl font-bold text-foreground">{totalClicks}</p>
              <p className="text-xs text-muted-foreground">Total Clicks</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <UserPlus className="h-5 w-5 text-primary mx-auto mb-1" />
              <p className="text-2xl font-bold text-foreground">{totalSignups}</p>
              <p className="text-xs text-muted-foreground">Total Signups</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <TrendingUp className="h-5 w-5 text-primary mx-auto mb-1" />
              <p className="text-2xl font-bold text-foreground">{conversionRate}%</p>
              <p className="text-xs text-muted-foreground">Conversion</p>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4" />
              Influencer Codes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : codes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No influencer codes yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead className="text-right">Signups</TableHead>
                    <TableHead className="text-right">Conv %</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {codes.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-mono text-xs">{c.ref_code}</TableCell>
                      <TableCell className="text-sm">{c.label || '—'}</TableCell>
                      <TableCell className="text-right">{c.clicks}</TableCell>
                      <TableCell className="text-right">{c.signups}</TableCell>
                      <TableCell className="text-right">
                        {c.clicks > 0 ? ((c.signups / c.clicks) * 100).toFixed(1) : '0'}%
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(c.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
